import { Injectable, inject, signal } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { environment } from '../../../environments/environment';
import { DispatchStation } from '../models/kitchen.model';
import { Order } from '../models/order.model';
import { RappiOrderReceivedEvent } from '../models/rappi.model';
import { SessionExpiredHandlerService } from './session-expired-handler.service';

export interface OrderDeletedEvent {
  orderId: string;
}

export interface OrderStatusChangedEvent {
  orderId: string;
  newStatus: string;
  sedeId?: string | null;
  timestamp: string;
}

export interface OrderReadyForKitchenEvent {
  orderId: string;
  sedeId?: string | null;
  timestamp: string;
}

export interface OrderCreatedFromKioskEvent {
  orderId: string;
  orderNumber: string;
  kioskName: string;
  sedeId?: string | null;
  timestamp: string;
}

/**
 * El aviso "llego un traslado" (evento SignalR "OrderTransferredIn"), emitido al grupo de la
 * sede DESTINO. A diferencia de los demas eventos per-sede, la sede del payload viaja en
 * `targetSedeId` (el evento describe a donde LLEGO el pedido), no en `sedeId`.
 */
export interface OrderTransferredInEvent {
  orderId: string;
  orderNumber: string;
  dailyCommandNumber: number | null;
  sourceSedeName: string;
  targetSedeId: string;
  performedByName: string;
  reason: string | null;
  shortfalls: { supplyName: string; unitOfMeasure: string; missingQuantity: number }[];
  timestamp: string;
}

/**
 * Emitido por CashRegisterHub SOLO cuando el CLIENTE (app) escribe un mensaje en el
 * chat de un pedido. Lleva el conteo de no leidos para que el POS actualice el badge
 * del pedido sin refetch. No se emite cuando responde el restaurante.
 */
export interface OrderChatCustomerMessageEvent {
  orderId: string;
  orderNumber: string;
  sedeId?: string | null;
  unreadCustomerChatCount: number;
}

export interface OrderItemStatusChangedEvent {
  orderId: string;
  itemId: string;
  newStatus: string;
  station?: string;
  sedeId?: string | null;
  timestamp: string;
}

export interface SurveySubmittedEvent {
  orderId: string;
  sedeId?: string | null;
  timestamp: string;
}

export interface AfterPaymentSurveyRequestedEvent {
  orderId: string;
  sedeId?: string | null;
  waiterId?: string | null;
  tableNumber?: string | null;
  customerName?: string | null;
  customerPhone?: string | null;
  timestamp: string;
}

@Injectable({ providedIn: 'root' })
export class RealtimeHubService {
  private sessionExpiredHandler = inject(SessionExpiredHandlerService);
  private connection: signalR.HubConnection | null = null;
  private kitchenConnection: signalR.HubConnection | null = null;
  /** Estaciones suscritas por la sesion actual. Se reaplican al (re)conectar
   * el kitchenHub para que la suscripcion sobreviva a un reconnect transparente. */
  private subscribedStations = new Set<DispatchStation>();
  /** Sedes a las que la conexion esta suscrita (grupos restaurant_{id}_sede_{sid}).
   * Cuando un POS de Sede A ya no quiere oir pedidos de Sede B, basta con que solo
   * tenga su sede activa aca. Los eventos del backend van solo al grupo per-sede. */
  private subscribedSedeIds = new Set<string>();
  /** Sede activa para el filtrado defensivo en cliente. Si tiene valor, eventos
   * con sedeId distinta se descartan en este servicio antes de emitir el signal.
   * Si es null = "todas las sedes" (Owner consolidado), no filtra. */
  private activeSedeId: string | null = null;
  cashRegisterUpdated = signal<number>(0);
  ordersUpdated = signal<number>(0);
  inventoryUpdated = signal<number>(0);
  rappiOrderReceived = signal<RappiOrderReceivedEvent | null>(null);
  // Delta updates: el backend emite OrderUpserted con el OrderDto completo y
  // OrderDeleted con el orderId. Los stores hacen upsert/remove local sin
  // re-fetch -> latencia <100ms percibida y cero carga al servidor.
  orderUpserted = signal<Order | null>(null);
  orderDeleted = signal<OrderDeletedEvent | null>(null);
  // Marca el momento del ultimo delta recibido. Los componentes que tienen un
  // refetch defensivo en `ordersUpdated` (tick legacy) lo usan para saltar el
  // refetch si ya recibieron el delta -- evita el doble refresh.
  lastDeltaAt = signal<number>(0);
  // Último evento recibido desde /hubs/kitchen. El signal cambia de referencia en cada emisión
  // para que los effects que dependen de él disparen aunque lleguen dos eventos idénticos seguidos.
  orderStatusChanged = signal<OrderStatusChangedEvent | null>(null);
  orderReadyForKitchen = signal<OrderReadyForKitchenEvent | null>(null);
  /** Emite cada vez que llega un pedido nuevo desde un kiosko de autoservicio. */
  kioskOrderCreated = signal<OrderCreatedFromKioskEvent | null>(null);
  /** Emite cuando le llega a esta sede un pedido trasladado desde otra. Cambia de
   * referencia en cada emision para que dos traslados seguidos disparen dos toasts. */
  orderTransferredIn = signal<OrderTransferredInEvent | null>(null);
  /** Emite cuando el CLIENTE escribe en el chat de un pedido. El POS reproduce el
   * sonido de mensaje (via BackgroundOrdersMonitorService, tab-leader) y actualiza el
   * badge de no leidos del pedido sin refetch. Cambia de referencia en cada emision. */
  customerChatMessage = signal<OrderChatCustomerMessageEvent | null>(null);
  // Cambios a nivel item (con station) — cocina/barra los usan para refrescar SOLO
  // si la estacion del evento coincide con la vista activa.
  orderItemStatusChanged = signal<OrderItemStatusChangedEvent | null>(null);
  /** Emite cuando el cliente responde la encuesta publica. Lo escucha waiter-orders
   * para refrescar el badge en vivo sin esperar al polling. */
  surveySubmitted = signal<SurveySubmittedEvent | null>(null);
  /** Emite cuando se cobra un pedido de mesa y hay que pedirle al mesero la encuesta
   * "despues de cobrar". Lo escucha waiter-orders para abrir el dialog con el numero de mesa. */
  afterPaymentSurveyRequested = signal<AfterPaymentSurveyRequestedEvent | null>(null);

  /** Estado de cada hub para diagnostico en vivo (chip de cocina). Si el hub queda
   * en 'Disconnected'/'Reconnecting' mientras la pantalla deberia estar activa, los
   * eventos delta no llegan y la cola solo se refresca por polling. */
  cashHubState = signal<string>('Disconnected');
  kitchenHubState = signal<string>('Disconnected');

  /** Timestamp del ultimo reconnect exitoso (cualquiera de los dos hubs). Los
   * controllers que necesiten catchup tras un disconnect (ej. pos-polling)
   * pueden hacer effect sobre este signal y re-disparar su query principal.
   * Sin esto, cuando el hub se reconecta despues de varios minutos en background,
   * los eventos que ocurrieron durante el corte se pierden — el delta llega via
   * grupo pero los pedidos creados antes del re-join siguen viejos en el store. */
  reconnectedAt = signal<number>(0);

  /** Listener de visibilitychange registrado una sola vez por instancia para
   * forzar reconnect al volver de background. En SSR (document undefined) se
   * salta sin error. Guardamos la referencia para poder removeEventListener
   * limpio desde disconnect() — sin ref guardada, una arrow inline quedaria
   * colgada al document aunque el servicio "se apague" por logout. */
  private visibilityListenerRegistered = false;
  private visibilityChangeHandler: (() => void) | null = null;

  private syncHubStates(): void {
    this.cashHubState.set(this.connection?.state ?? 'Disconnected');
    this.kitchenHubState.set(this.kitchenConnection?.state ?? 'Disconnected');
  }

  /** Politica de retry custom: los 4 primeros intentos copian el comportamiento
   * default de SignalR (~45s en total) para reaccionar rapido a glitches cortos.
   * A partir del 5o intento, reintenta CADA 60s indefinidamente. El default de
   * SignalR aborta tras 4 intentos -> el hub queda 'Disconnected' permanente y
   * pantallas operativas (POS/cocina/barra) dejan de recibir deltas hasta que el
   * usuario refresque la pagina. Con esta politica nunca tira la toalla. */
  private buildRetryPolicy(): signalR.IRetryPolicy {
    const initialDelaysMs = [0, 2000, 10000, 30000];
    const longRetryIntervalMs = 60000;
    return {
      nextRetryDelayInMilliseconds: ctx =>
        ctx.previousRetryCount < initialDelaysMs.length
          ? initialDelaysMs[ctx.previousRetryCount]
          : longRetryIntervalMs
    };
  }

  /** Registrado una sola vez. Al volver la pestaña a foreground, si algun hub
   * quedo 'Disconnected' (ej. el navegador suspendio el WS en background y la
   * politica de retry se quedo esperando el siguiente tick de 60s), forzamos
   * un .start() inmediato para que el usuario no espere ese minuto. */
  private registerVisibilityListenerIfNeeded(): void {
    if (this.visibilityListenerRegistered) return;
    if (typeof document === 'undefined') return;
    this.visibilityChangeHandler = () => {
      if (document.visibilityState === 'visible') {
        this.forceReconnectIfDisconnected();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
    this.visibilityListenerRegistered = true;
  }

  /** Quita el listener de visibilitychange. Se llama desde disconnect() para
   * evitar que el handler quede vivo en document apuntando a `this` despues de
   * un logout — sin esto el GC no libera el servicio si la app deja referencias. */
  private unregisterVisibilityListener(): void {
    if (typeof document === 'undefined') return;
    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
      this.visibilityChangeHandler = null;
    }
    this.visibilityListenerRegistered = false;
  }

  /** Reactiva manualmente cualquier hub que quedo en 'Disconnected'. La politica
   * de retry de SignalR no entra cuando el estado es Disconnected "final"
   * (close sin recovery) — hay que llamar .start() de nuevo. Usado al volver de
   * background tab y disponible por si algun caller quiere disparar catchup. */
  forceReconnectIfDisconnected(): void {
    if (this.connection && this.connection.state === signalR.HubConnectionState.Disconnected) {
      this.connection.start()
        .then(() => {
          this.reapplySedeSubscriptions(this.connection);
          this.syncHubStates();
          this.reconnectedAt.set(Date.now());
        })
        .catch(err => {
          console.error('Realtime hub force reconnect error:', err);
          this.handleHubCloseError(err);
          this.syncHubStates();
        });
    }
    if (this.kitchenConnection && this.kitchenConnection.state === signalR.HubConnectionState.Disconnected) {
      this.kitchenConnection.start()
        .then(() => {
          this.reapplyStationSubscriptions();
          this.reapplySedeSubscriptions(this.kitchenConnection);
          this.syncHubStates();
          this.reconnectedAt.set(Date.now());
        })
        .catch(err => {
          console.error('Kitchen hub force reconnect error:', err);
          this.handleHubCloseError(err);
          this.syncHubStates();
        });
    }
  }

  connect(token: string): void {
    // Registrar el listener de visibilitychange aca (no en constructor) porque el
    // servicio puede instanciarse antes de que document este listo en algunos entry
    // points. La bandera interna garantiza que no se duplique.
    this.registerVisibilityListenerIfNeeded();

    if (!this.connection) {
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(`${environment.apiUrl.replace(/\/api\/?$/, '')}/hubs/cashregister`, {
          accessTokenFactory: () => token,
          // WebSocket-only: el fallback a long-polling consume conexiones HTTP del
          // pool del navegador (~6/dominio). Bajo alto volumen eso ayuda a agotarlo y
          // congela la pantalla. Si WS no estuviera disponible, el polling de 30s de
          // cada pantalla operativa cubre como respaldo.
          transport: signalR.HttpTransportType.WebSockets
        })
        .withAutomaticReconnect(this.buildRetryPolicy())
        .build();

      this.connection.on('CashRegisterUpdated', () => {
        this.cashRegisterUpdated.update(v => v + 1);
      });

      this.connection.on('OrdersUpdated', () => {
        this.ordersUpdated.update(v => v + 1);
      });

      this.connection.on('OrderUpserted', (order: Order) => {
        if (!order || !order.id) return;
        // Defensa cross-sede: si esta conexion tiene sede activa y el order es de
        // otra sede, descartar. Cubre el caso en que el cliente este suscrito al
        // grupo legacy "restaurant_{id}" o a multiples grupos de sede pero el
        // componente solo quiere ver una sede.
        if (this.activeSedeId && order.sedeId && order.sedeId !== this.activeSedeId) return;
        this.lastDeltaAt.set(Date.now());
        // Cambiamos referencia siempre para que los effects que dependen del
        // signal disparen aunque la orden anterior tuviera el mismo Id.
        this.orderUpserted.set({ ...order });
      });

      this.connection.on('OrderDeleted', (payload: OrderDeletedEvent) => {
        if (!payload || !payload.orderId) return;
        this.lastDeltaAt.set(Date.now());
        this.orderDeleted.set({ orderId: payload.orderId });
      });

      this.connection.on('InventoryUpdated', () => {
        this.inventoryUpdated.update(v => v + 1);
      });

      this.connection.on('RappiOrderReceived', (payload: RappiOrderReceivedEvent) => {
        this.rappiOrderReceived.set(payload);
      });

      this.connection.on('OrderCreatedFromKiosk', (payload: OrderCreatedFromKioskEvent) => {
        if (!payload || !payload.orderId) return;
        if (this.activeSedeId && payload.sedeId && payload.sedeId !== this.activeSedeId) return;
        this.kioskOrderCreated.set({ ...payload });
      });

      // El campo de sede aca es `targetSedeId` (a donde LLEGO el pedido), no `sedeId`: el
      // evento lo emite el backend al grupo de la sede destino, y esta es la unica defensa
      // cross-sede — sin ella, un POS de otra sede mostraria el toast de un traslado ajeno.
      this.connection.on('OrderTransferredIn', (payload: OrderTransferredInEvent) => {
        if (!payload || !payload.orderId) return;
        if (this.activeSedeId && payload.targetSedeId && payload.targetSedeId !== this.activeSedeId) return;
        this.orderTransferredIn.set({ ...payload });
      });

      this.connection.on('OrderChatCustomerMessage', (payload: OrderChatCustomerMessageEvent) => {
        if (!payload || !payload.orderId) return;
        // Defensa cross-sede: un POS con sede activa no debe sonar por el chat de otra sede.
        if (this.activeSedeId && payload.sedeId && payload.sedeId !== this.activeSedeId) return;
        this.customerChatMessage.set({ ...payload });
      });

      this.connection.onreconnecting(() => this.syncHubStates());
      this.connection.onreconnected(() => {
        this.reapplySedeSubscriptions(this.connection);
        this.syncHubStates();
        // Emite el timestamp para que los controllers que escuchan (pos-polling,
        // listas operativas) hagan catchup de los eventos que ocurrieron mientras
        // el hub estuvo caido. Sin esto, los re-joins recuperan eventos futuros
        // pero no los pasados.
        this.reconnectedAt.set(Date.now());
      });
      // Si el reconnect agota intentos por token muerto (401 en negociacion),
      // SignalR llama onclose con el error. Detectamos eso y delegamos al
      // SessionExpiredHandler — sin esto, mesero/POS en celular vuelven de
      // background con JWT vencido y quedan en pantalla pero sin eventos.
      this.connection.onclose(err => {
        this.syncHubStates();
        this.handleHubCloseError(err);
      });

      this.connection.start()
        .then(() => {
          this.reapplySedeSubscriptions(this.connection);
          this.syncHubStates();
        })
        .catch(err => {
          console.error('Realtime hub error:', err);
          this.handleHubCloseError(err);
          this.connection = null;
          this.syncHubStates();
        });
    }

    if (!this.kitchenConnection) {
      this.kitchenConnection = new signalR.HubConnectionBuilder()
        .withUrl(`${environment.apiUrl.replace(/\/api\/?$/, '')}/hubs/kitchen`, {
          accessTokenFactory: () => token,
          // Ver nota en /hubs/cashregister: WebSocket-only para no competir por el pool HTTP.
          transport: signalR.HttpTransportType.WebSockets
        })
        .withAutomaticReconnect(this.buildRetryPolicy())
        .build();

      this.kitchenConnection.on('OrderStatusChanged', (payload: OrderStatusChangedEvent) => {
        this.orderStatusChanged.set(payload);
      });

      this.kitchenConnection.on('OrderReadyForKitchen', (payload: OrderReadyForKitchenEvent) => {
        this.orderReadyForKitchen.set(payload);
      });

      this.kitchenConnection.on('OrderItemStatusChanged', (payload: OrderItemStatusChangedEvent) => {
        // Defensa cross-sede: descartar eventos de otras sedes cuando el cliente
        // ya escogio una sede activa (POS, cocina y barra ven solo lo suyo).
        if (this.activeSedeId && payload?.sedeId && payload.sedeId !== this.activeSedeId) return;
        this.orderItemStatusChanged.set(payload);
      });

      this.kitchenConnection.on('SurveySubmitted', (payload: SurveySubmittedEvent) => {
        if (!payload || !payload.orderId) return;
        if (this.activeSedeId && payload.sedeId && payload.sedeId !== this.activeSedeId) return;
        this.surveySubmitted.set({ ...payload });
      });

      this.kitchenConnection.on('AfterPaymentSurveyRequested', (payload: AfterPaymentSurveyRequestedEvent) => {
        if (!payload || !payload.orderId) return;
        if (this.activeSedeId && payload.sedeId && payload.sedeId !== this.activeSedeId) return;
        this.afterPaymentSurveyRequested.set({ ...payload });
      });

      this.kitchenConnection.onreconnecting(() => this.syncHubStates());
      this.kitchenConnection.onreconnected(() => {
        this.reapplyStationSubscriptions();
        this.reapplySedeSubscriptions(this.kitchenConnection);
        this.syncHubStates();
        // Mismo motivo que el cashregister hub: avisar para catchup de eventos
        // perdidos durante el downtime.
        this.reconnectedAt.set(Date.now());
      });
      this.kitchenConnection.onclose(err => {
        this.syncHubStates();
        this.handleHubCloseError(err);
      });

      this.kitchenConnection.start()
        .then(() => {
          this.reapplyStationSubscriptions();
          this.reapplySedeSubscriptions(this.kitchenConnection);
          this.syncHubStates();
        })
        .catch(err => {
          console.error('Kitchen hub error:', err);
          this.handleHubCloseError(err);
          this.kitchenConnection = null;
          this.syncHubStates();
        });
    }

  }

  private async reapplyStationSubscriptions(): Promise<void> {
    if (!this.kitchenConnection || this.kitchenConnection.state !== signalR.HubConnectionState.Connected) {
      return;
    }
    for (const station of this.subscribedStations) {
      try {
        await this.kitchenConnection.invoke('JoinStationGroup', station);
      } catch (err) {
        console.error('Reapply JoinStationGroup error:', err);
      }
    }
  }

  /**
   * Suscribe esta conexion al grupo SignalR de la estacion (Cocina/Barra) del
   * restaurante actual. Los eventos OrderItemStatusChanged se entregan al grupo
   * de la estacion correspondiente — la vista de Cocina no recibe ruido de Barra.
   *
   * Devuelve true si la suscripcion se realizo, false si la conexion no estaba lista.
   */
  async joinStationGroup(station: DispatchStation): Promise<boolean> {
    this.subscribedStations.add(station);
    let joined = false;
    if (this.kitchenConnection && this.kitchenConnection.state === signalR.HubConnectionState.Connected) {
      try {
        await this.kitchenConnection.invoke('JoinStationGroup', station);
        joined = true;
      } catch (err) {
        console.error('JoinStationGroup error:', err);
      }
    }
    // Si la conexion aun no esta lista, reapplyStationSubscriptions hara el invoke
    // cuando el start() resuelva o un reconnect ocurra.
    return joined;
  }

  async leaveStationGroup(station: DispatchStation): Promise<void> {
    this.subscribedStations.delete(station);
    if (this.kitchenConnection && this.kitchenConnection.state === signalR.HubConnectionState.Connected) {
      try {
        await this.kitchenConnection.invoke('LeaveStationGroup', station);
      } catch (err) {
        console.error('LeaveStationGroup error:', err);
      }
    }
  }

  /**
   * Setea la sede activa de la conexion y maneja join/leave automaticamente.
   * Pasar null limpia (vista consolidada — Owner — sin filtrar nada).
   * Componentes deben llamarlo al login y cada vez que el dropdown de sede cambie.
   */
  async setActiveSede(sedeId: string | null): Promise<void> {
    const previous = this.activeSedeId;
    this.activeSedeId = sedeId;

    // Si cambia la sede, salir de la anterior y entrar en la nueva.
    if (previous && previous !== sedeId) {
      await this.leaveSedeGroup(previous);
    }
    if (sedeId && sedeId !== previous) {
      await this.joinSedeGroup(sedeId);
    }
  }

  /**
   * Define el alcance de sedes para vistas que pueden estar en "Todas las sedes"
   * (Owner consolidado). A diferencia de setActiveSede (una sola sede):
   *  - activeSedeId con valor -> escucha SOLO esa sede (grupo + filtro cliente).
   *  - activeSedeId null       -> consolidado: se une a TODAS las sedes para recibir
   *    sus eventos por-sede (OrderUpserted/OrdersUpdated/CashRegisterUpdated van solo
   *    al grupo restaurant_{id}_sede_{sid}) y NO filtra en cliente.
   * Hace join/leave incremental sobre subscribedSedeIds, asi sobrevive reconnects
   * via reapplySedeSubscriptions.
   */
  async setSedeScope(activeSedeId: string | null, allSedeIds: string[]): Promise<void> {
    this.activeSedeId = activeSedeId;
    const desired = new Set(activeSedeId ? [activeSedeId] : allSedeIds);
    const toLeave = [...this.subscribedSedeIds].filter(id => !desired.has(id));
    const toJoin = [...desired].filter(id => !this.subscribedSedeIds.has(id));
    await Promise.all([
      ...toLeave.map(id => this.leaveSedeGroup(id)),
      ...toJoin.map(id => this.joinSedeGroup(id)),
    ]);
  }

  /**
   * Suscribe ambos hubs (cashregister + kitchen) al grupo de la sede.
   * Se invoca implicitamente desde setActiveSede; expuesto por si algun caller
   * quiere subscribir varias sedes simultaneas (ej. dashboard consolidado).
   *
   * IMPORTANTE: `subscribedSedeIds.add` debe ocurrir ANTES de los invokes para
   * que si el hub esta en estado Connecting/Reconnecting, los reapply hooks
   * (start().then() y onreconnected) lo retomen cuando llegue a Connected.
   * Sin esto, el caller setActiveSede() no espera a que el hub este listo y la
   * sede queda sin grupo -> POS de Sede A no recibe deltas de su propia sede.
   */
  async joinSedeGroup(sedeId: string): Promise<void> {
    this.subscribedSedeIds.add(sedeId);
    await Promise.all([
      this.invokeOnHub(this.connection, 'JoinSedeGroup', sedeId),
      this.invokeOnHub(this.kitchenConnection, 'JoinSedeGroup', sedeId)
    ]);
  }

  async leaveSedeGroup(sedeId: string): Promise<void> {
    this.subscribedSedeIds.delete(sedeId);
    await Promise.all([
      this.invokeOnHub(this.connection, 'LeaveSedeGroup', sedeId),
      this.invokeOnHub(this.kitchenConnection, 'LeaveSedeGroup', sedeId)
    ]);
  }

  private async reapplySedeSubscriptions(hub: signalR.HubConnection | null): Promise<void> {
    if (!hub || hub.state !== signalR.HubConnectionState.Connected) return;
    for (const sedeId of this.subscribedSedeIds) {
      try {
        await hub.invoke('JoinSedeGroup', sedeId);
      } catch (err) {
        console.error('Reapply JoinSedeGroup error:', err);
      }
    }
  }

  /**
   * Invoca un metodo del hub respetando el estado de conexion. Si el hub esta:
   *  - Connected: invoca inmediatamente.
   *  - Connecting/Reconnecting: el invoke SIGNALR cliente lo encola, pero por
   *    seguridad lo saltamos aca y dejamos que reapplySedeSubscriptions lo retome
   *    cuando llegue a Connected. Esto evita errores "InvalidOperationException:
   *    The 'invoke' method cannot be called if the connection is not in the
   *    'Connected' state".
   *  - Disconnected: salta (no hay nada que hacer). El proximo start() lo retomara
   *    si fue un join al subscribedSedeIds.
   *
   * Antes este metodo solo hacia early return cuando state != Connected. Esto
   * funcionaba porque el caller ya guarda en subscribedSedeIds antes, y el
   * .then() del start() y el onreconnected reaplican desde ahi. Mantenemos la
   * semantica para no romper callers, pero documentamos el porque.
   */
  private async invokeOnHub(hub: signalR.HubConnection | null, method: string, ...args: unknown[]): Promise<void> {
    if (!hub || hub.state !== signalR.HubConnectionState.Connected) return;
    try {
      await hub.invoke(method, ...args);
    } catch (err) {
      console.error(`${method} error:`, err);
    }
  }

  disconnect(): void {
    this.connection?.stop();
    this.connection = null;
    this.kitchenConnection?.stop();
    this.kitchenConnection = null;
    this.subscribedSedeIds.clear();
    this.subscribedStations.clear();
    this.activeSedeId = null;
    this.unregisterVisibilityListener();
    this.syncHubStates();
  }

  private handleHubCloseError(err: Error | unknown): void {
    if (!err) return;
    const message = err instanceof Error ? err.message : String(err);
    if (/401|unauthorized/i.test(message)) {
      this.sessionExpiredHandler.handle();
    }
  }
}
